import React, { useEffect, useState } from "react";
import { FiltersButton, TableHeader } from "@services/ui-components";
import { useTranslation } from "react-i18next";
import { Button, Col, Row, Tabs } from "react-bootstrap";
import { Tab } from "bootstrap";
import BookingRequests from "./BookingRequests";
import { ReservationStatus } from "@me-team/host/meBooking/graphql/types";
import DatePicker from "@services/ui-components/src/DatePicker/DatePicker";
import moment from "moment/moment";
import { currentBranchIdVar } from "@me-team/host/main/globalVar/state";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useGetReservationByIdQuery } from "@me-team/host/meBooking/restaurantBooking/graphql/restaurantBooking.hooks";
import { apolloClientMeBooking } from "@me-team/host/src/apollo/apollo-client";
import BookingModal from "./BookingModal";

const RestaurantBooking: React.FC = () => {
    const { t } = useTranslation();
    const [filter, setFilter] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [currentFilter] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [dateTerm, setDateTerm] = useState<Date | null>(null);
    const toggleFilters = () => setShowFilters(!showFilters);
    const formattedDate = dateTerm ? moment(dateTerm).format("YYYY-MM-DD") : "";
    const [activeTab, setActiveTab] = useState("AllRequests");
    const handleApplyFilter = () => {
        setFilter(currentFilter);
    };
    const { branchId } = useParams();
    const navigate = useNavigate();
    const currentBranchId = branchId ?? currentBranchIdVar();
    const [searchParams, setSearchParams] = useSearchParams();
    const hasDetailsModal = searchParams.has("detailsModal");
    const detailsId = searchParams.get("detailsModal");
    const { data: reservationDataById } = useGetReservationByIdQuery({
        variables: {
            id: Number(detailsId),
        },
        skip: !detailsId,
        client: apolloClientMeBooking,
    });
    const reservationInfo = reservationDataById?.reservationById ?? null;
    const [showModal, setShowModal] = useState(false);
    const refetchReservations = async () => {
        await apolloClientMeBooking.refetchQueries({
            include: ["GetReservations"],
        });
    };

    useEffect(() => {
        if (reservationInfo) {
            setShowModal(true);
        }
    }, [reservationInfo]);

    const handleClearFilter = () => {
        setSearchTerm("");
        setDateTerm(null);
    };

    useEffect(() => {
        if (!branchId) {
            navigate(`/me-booking/restaurant-booking/${currentBranchId}`);
        }
        if (hasDetailsModal) {
            navigate(`/me-booking/restaurant-booking/${currentBranchId}?${searchParams}`);
        }
    }, [branchId, currentBranchId, hasDetailsModal, navigate, searchParams]);

    return (
        <>
            <TableHeader
                title={t("Booking")}
                paths={[
                    { path: "", label: "ME-Booking" },
                    { path: "", label: "Booking" },
                ]}
                buttons={
                    <Row className="gx-3 justify-content-end mt-3 mt-lg-0">
                        <Col xs={5} md={6} lg={5} xxl={4}>
                            <FiltersButton
                                filter={filter}
                                showFilters={showFilters}
                                onClick={toggleFilters}
                                classesText="d-none d-sm-inline-block"
                            />
                        </Col>
                    </Row>
                }
                showFilters={showFilters}
                colSize={7}
                btnColSize={5}
            >
                <Row className="gx-4 pb-6">
                    <Col lg={8} xl={9}>
                        <Row className="gx-3">
                            <Col md={6}>
                                <div className="position-relative mb-3 mb-lg-0">
                                    <input
                                        type="text"
                                        className="form-control rounded-1"
                                        placeholder={t("Search by name, phone or email")}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        value={searchTerm}
                                    />
                                    <i className="bi bi-search position-absolute top-50 end-0 translate-middle-y text-dark me-3"></i>
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="position-relative mb-3 mb-lg-0 w-100">
                                    <DatePicker
                                        className={`form-control w-100 cursor-pointer`}
                                        wrapperClassName="w-100"
                                        placeholderText={t("Date of reservation")}
                                        onChange={(date: Date | null) => setDateTerm(date)}
                                        dateFormat="dd.MM.yyyy"
                                        selected={dateTerm}
                                    />
                                    <i
                                        className="bi bi-chevron-down position-absolute top-50 end-0 translate-middle-y text-dark me-3"
                                        style={{ pointerEvents: "none" }}
                                    ></i>
                                </div>
                            </Col>
                        </Row>
                    </Col>
                    <Col lg={4} xl={3}>
                        <Row className="gx-3 h-100">
                            <Col xs={6}>
                                <Button
                                    id="branch-clear-button"
                                    variant="outline-dark"
                                    className="border-grey w-100 h-100 fw-normal rounded-1"
                                    onClick={handleClearFilter}
                                >
                                    {t("clear")}
                                </Button>
                            </Col>
                            <Col xs={6}>
                                <Button
                                    id="branch-apply-button"
                                    variant="primary"
                                    className="w-100 h-100 rounded-1 fw-normal px-2"
                                    onClick={handleApplyFilter}
                                >
                                    {t("apply")}
                                </Button>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </TableHeader>
            <Tabs
                defaultActiveKey="PendingRequests"
                id="justify-tab-example"
                className="mb-4"
                justify
                onSelect={(k) => setActiveTab(k || "PendingRequests")}
            >
                <Tab eventKey="PendingRequests" title={t("Pending")}>
                    <BookingRequests
                        statuses={[ReservationStatus.Pending, ReservationStatus.WaitingForOpening]}
                        dateTerm={formattedDate}
                        searchTerm={searchTerm}
                        activeTab={activeTab}
                    />
                </Tab>
                <Tab eventKey="ProcessRequests" title={t("In progress")}>
                    <BookingRequests
                        statuses={[ReservationStatus.InProgress]}
                        dateTerm={formattedDate}
                        searchTerm={searchTerm}
                        activeTab={activeTab}
                    />
                </Tab>
                <Tab eventKey="ApprovedRequests" title={t("Approved")}>
                    <BookingRequests
                        statuses={[ReservationStatus.Confirmed]}
                        dateTerm={formattedDate}
                        searchTerm={searchTerm}
                        activeTab={activeTab}
                    />
                </Tab>
                <Tab eventKey="RejectedRequests" title={t("Rejected")}>
                    <BookingRequests
                        statuses={[ReservationStatus.Rejected, ReservationStatus.NoConnection]}
                        dateTerm={formattedDate}
                        searchTerm={searchTerm}
                        activeTab={activeTab}
                    />
                </Tab>
                <Tab eventKey="CanceledRequests" title={t("CanceledByUser")}>
                    <BookingRequests
                        statuses={[ReservationStatus.CancelledByUser]}
                        dateTerm={formattedDate}
                        searchTerm={searchTerm}
                        activeTab={activeTab}
                    />
                </Tab>
                <Tab eventKey="AllRequests" title={t("All Requests")}>
                    <BookingRequests
                        searchTerm={searchTerm}
                        dateTerm={formattedDate}
                        statuses={[]}
                        activeTab={activeTab}
                    />
                </Tab>
            </Tabs>

            <BookingModal
                show={showModal}
                handleClose={() => {
                    setShowModal(false);
                    searchParams.delete("detailsModal");
                    setSearchParams(searchParams, { replace: true });
                }}
                reservation={reservationInfo}
                refetchReservations={refetchReservations}
            />
        </>
    );
};
export default RestaurantBooking;
